/* ══════════════════════════════════════════════════════════════
   News Search — Filter live headlines from the news service
   ══════════════════════════════════════════════════════════════ */

import { fetchNews, type NewsItem } from '../services/news-feed';

function escapeHtml(value?: string): string {
  if (!value) return '';
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function toTimestamp(pubDate?: string): number {
  if (!pubDate) return 0;
  const ts = new Date(pubDate).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function normalizeCategory(item: NewsItem): string {
  return (item.category || 'general').toLowerCase();
}

function matchesCategoryFilter(item: NewsItem, selectedCategory: string): boolean {
  if (!selectedCategory) return true;

  const category = normalizeCategory(item);
  if (selectedCategory === 'policy') return category === 'policy' || category === 'politics' || category === 'general';
  if (selectedCategory === 'markets') return category === 'markets' || category === 'market' || category === 'business';
  if (selectedCategory === 'global') return category === 'global' || category === 'world' || category === 'top' || category === 'general';
  return category === selectedCategory;
}

function dedupeLatest(items: NewsItem[]): NewsItem[] {
  const unique = new Map<string, NewsItem>();
  items.forEach(item => {
    const key = item.title.trim().toLowerCase();
    if (!key) return;
    const existing = unique.get(key);
    if (!existing || toTimestamp(item.pubDate) > toTimestamp(existing.pubDate)) {
      unique.set(key, item);
    }
  });
  return Array.from(unique.values()).sort((a, b) => toTimestamp(b.pubDate) - toTimestamp(a.pubDate));
}

function getCutoffMs(timeframe: string): number {
  const now = Date.now();
  if (timeframe === '24h') return now - (24 * 60 * 60 * 1000);
  if (timeframe === '7d') return now - (7 * 24 * 60 * 60 * 1000);
  return now - (30 * 24 * 60 * 60 * 1000);
}

function computeRelevance(item: NewsItem, query: string): number {
  if (!query) return 0;
  const haystack = `${item.title} ${item.description} ${item.source} ${item.category}`.toLowerCase();
  const terms = query.split(/\s+/).filter(Boolean);
  return terms.reduce((score, term) => score + (haystack.includes(term) ? 1 : 0), 0);
}

function renderResults(
  container: HTMLElement,
  items: NewsItem[],
  query: string,
  category: string,
  timeframe: string
): void {
  const resultsEl = container.querySelector('.search-results');
  const statsEl = container.querySelector('.search-stats');
  if (!resultsEl || !statsEl) return;

  const cutoff = getCutoffMs(timeframe);
  const normalizedQuery = query.trim().toLowerCase();

  const filtered = items
    .filter(item => {
      const matchesCategory = matchesCategoryFilter(item, category);
      const matchesTime = toTimestamp(item.pubDate) >= cutoff || !item.pubDate;
      const relevance = computeRelevance(item, normalizedQuery);
      const matchesQuery = !normalizedQuery || relevance > 0;
      return matchesCategory && matchesTime && matchesQuery;
    })
    .sort((a, b) => {
      const relevanceDiff = computeRelevance(b, normalizedQuery) - computeRelevance(a, normalizedQuery);
      if (relevanceDiff !== 0) return relevanceDiff;
      return toTimestamp(b.pubDate) - toTimestamp(a.pubDate);
    });

  if (filtered.length === 0) {
    resultsEl.innerHTML = `
      <div class="search-result-item glass-card" style="cursor:default;">
        <div class="result-content">
          <h4>No live headlines match this filter</h4>
          <span class="result-meta">Try a broader query, category, or timeframe.</span>
        </div>
      </div>
    `;
    statsEl.innerHTML = `<small>0 results · ${items.length} live headlines indexed</small>`;
    return;
  }

  resultsEl.innerHTML = filtered.map(item => {
    const label = (item.category || 'live').slice(0, 10).toUpperCase();
    return `
      <a class="search-result-item glass-card" href="${escapeHtml(item.link || '#')}" target="_blank" rel="noreferrer noopener">
        <span class="result-score">${escapeHtml(label)}</span>
        <div class="result-content">
          <h4>${escapeHtml(item.title)}</h4>
          <span class="result-meta">${escapeHtml(item.source || 'Newswire')} · ${escapeHtml(item.timeAgo || 'recent')}</span>
        </div>
      </a>
    `;
  }).join('');

  statsEl.innerHTML = `<small>${filtered.length} results · ${items.length} live headlines indexed</small>`;
}

export function renderNewsSearch(container: HTMLElement): void {
  container.innerHTML = `
    <div class="news-search">
      <div class="search-bar glass-card">
        <span class="search-icon">🔍</span>
        <input type="text" id="news-search-input" placeholder="Filter live headlines, companies, sectors..." class="search-input" />
        <div class="search-filters">
          <select id="search-category" class="search-select">
            <option value="">All Categories</option>
            <option value="policy">Policy</option>
            <option value="earnings">Earnings</option>
            <option value="markets">Markets</option>
            <option value="ipo">IPO</option>
            <option value="global">Global</option>
          </select>
          <select id="search-timeframe" class="search-select">
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>
      <div class="search-results">
        <div class="search-result-item glass-card" style="cursor:default;">
          <div class="result-content">
            <h4>Loading live headlines...</h4>
            <span class="result-meta">Fetching business, politics, and top stories.</span>
          </div>
        </div>
      </div>
      <div class="search-stats">
        <small>Connecting to live news feeds...</small>
      </div>
    </div>`;

  const input = container.querySelector('#news-search-input') as HTMLInputElement | null;
  const categorySelect = container.querySelector('#search-category') as HTMLSelectElement | null;
  const timeframeSelect = container.querySelector('#search-timeframe') as HTMLSelectElement | null;

  const applyFilters = (items: NewsItem[]) => {
    renderResults(
      container,
      items,
      input?.value || '',
      categorySelect?.value || '',
      timeframeSelect?.value || '24h'
    );
  };

  Promise.allSettled([
    fetchNews({ category: 'business', size: 10, maxFeeds: 12, cacheTTL: 60 }),
    fetchNews({ category: 'politics', size: 10, maxFeeds: 12, cacheTTL: 60 }),
    fetchNews({ category: 'top', size: 10, maxFeeds: 12, cacheTTL: 60 }),
  ]).then(results => {
    const merged: NewsItem[] = [];
    results.forEach(result => {
      if (result.status === 'fulfilled') merged.push(...result.value);
    });

    const liveItems = dedupeLatest(merged);
    if (liveItems.length === 0) {
      const resultsEl = container.querySelector('.search-results');
      const statsEl = container.querySelector('.search-stats');
      if (resultsEl) {
        resultsEl.innerHTML = `
          <div class="search-result-item glass-card" style="cursor:default;">
            <div class="result-content">
              <h4>Live news search unavailable</h4>
              <span class="result-meta">No headline feed responded right now.</span>
            </div>
          </div>
        `;
      }
      if (statsEl) statsEl.innerHTML = '<small>0 results · live feed unavailable</small>';
      return;
    }

    input?.addEventListener('input', () => applyFilters(liveItems));
    categorySelect?.addEventListener('change', () => applyFilters(liveItems));
    timeframeSelect?.addEventListener('change', () => applyFilters(liveItems));
    applyFilters(liveItems);
  }).catch(() => {
    const resultsEl = container.querySelector('.search-results');
    const statsEl = container.querySelector('.search-stats');
    if (resultsEl) {
      resultsEl.innerHTML = `
        <div class="search-result-item glass-card" style="cursor:default;">
          <div class="result-content">
            <h4>Live news search unavailable</h4>
            <span class="result-meta">Headline feeds failed to load.</span>
          </div>
        </div>
      `;
    }
    if (statsEl) statsEl.innerHTML = '<small>0 results · live feed unavailable</small>';
  });
}
